import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import MaterialTable from 'material-table';
import { TYPE_FAIL, TYPE_SUCCESS, INTERVAL } from 'assets/constants/Constants';
import { DELIVERY_ICON, CALENDER_ICON, EDIT_ICON, EYE_ICON } from 'assets/constants/Icons';
import ShowToast from 'components/ShowToast';
import Spinner from 'components/Spinner';
import { getAllDeliveryTrails, createDeliveryTrail } from 'lib/axiosUtils';
import { 
    ThemeProvider, createTheme, Chip, LinearProgress, Box, Typography,
    Dialog, DialogContent, IconButton, Grid, Button, Stepper, Step, StepLabel, StepContent
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import moment from 'moment';

import './delivery_trail.css';

const MinimalStepIcon = (props) => {
    const { active, completed } = props;
    return (
        <div
            style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: completed ? '#4caf50' : active ? '#2d4243' : '#e0e0e0',
                border: active ? '2px solid rgba(45, 66, 67, 0.3)' : 'none',
                boxShadow: active ? '0 0 0 4px rgba(45, 66, 67, 0.1)' : 'none',
            }}
        />
    );
};

const DeliveryTrailDashboard = () => {
    const history = useHistory();
    const [trailList, setTrailList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);

    const handleOpen = (rowData) => {
        setSelectedRowData(rowData);
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setSelectedRowData(null);
    };
    
    const theme = createTheme({
        palette: {
            primary: { main: '#2d4243' },
            secondary: { main: '#dc004e' },
        },
        typography: { fontFamily: 'Roboto, sans-serif' }
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await getAllDeliveryTrails();
            if (response?.data?.result) {
                setTrailList(response.data.result);
            }
        } catch (error) {
            console.error('Error:', error);
            ShowToast('Failed to fetch data', TYPE_FAIL, INTERVAL);
        } finally {
            setIsLoading(false);
        }
    };

const handleAction = async (rowData) => {
    if (rowData.isStarted) {
        history.push(`/create-delivery-trail/${rowData.bom_id._id}`);
    } else {
        if (window.confirm('Are you sure you want to start this delivery trail?')) {
            try {
                const res = await createDeliveryTrail({}, { bom_id: rowData.bom_id._id });

                // FIX: axiosUtils returns res.data, not status
                if (res?.data?.result) {
                    ShowToast('Delivery Trail Started!', TYPE_SUCCESS, INTERVAL);
                    await fetchData();   // refresh table
                }

            } catch (err) {
                ShowToast('Failed to initiate trail', TYPE_FAIL, INTERVAL);
            }
        }
    }
};



    const columns = [
        { 
            title: 'Project / Fixture', 
            field: 'bom_id.fixture_name',
            
            // render: row => (
            //     <Box>
            //         <Typography variant="subtitle2">
            //             {row.bom_id?.fixture_name}
            //         </Typography>
            //         <Typography variant="caption" color="textSecondary">
            //             {row.bom_id?.fixture_document_number}
            //         </Typography>
            //     </Box>
            // )
        },
        { title: 'Customer', field: 'bom_id.customer_id.customer_name' },
 { 
            title: 'Progress', 
            field: 'progress',
            width: '20%',
            render: row => (
                <Box sx={{ width: '80%', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress 
                        variant="determinate" 
                        value={row.progress || 0} 
                        sx={{ 
                            flex: 1,
                            height: 4, 
                            borderRadius: 2,
                            bgcolor: '#f0f0f0',
                            '& .MuiLinearProgress-bar': { bgcolor: '#2d3d42' }
                        }}
                    />
                    <Typography variant="caption" color="textSecondary">{row.progress || 0}%</Typography>
                </Box>
            )
        },
        { 
            title: 'Stage', 
            field: 'current_step',
            render: row => (
                <span style={{ 
                    fontSize: '12px', 
                    fontWeight: 500, 
                    color: row.current_step === 'Completed' ? '#2e7d32' : '#2d3d42',
                    background: row.current_step === 'Completed' ? '#edf7ed' : '#f5f5f5',
                    padding: '4px 10px',
                    borderRadius: '4px'
                }}>
                    {row.current_step}
                </span>
            )
        },
        { 
            title: 'Last Updated', 
            field: 'last_updated',
            width: '10%',
            render: row => {
                if (!row.steps || !Array.isArray(row.steps)) return '-';
                
                const completedSteps = row.steps.filter(s => 
                    (s.status === 'Completed' || s.status === 'Skipped') && s.completion_date
                );
                
                if (completedSteps.length === 0) return '-';
                
                completedSteps.sort((a, b) => new Date(b.completion_date) - new Date(a.completion_date));
                
                return moment(completedSteps[0].completion_date).format('DD MMM YYYY');
            }
        }
    ];

    return (

        (isLoading ? <Spinner /> : (
        <div className="main-content">
            <div className="pagetitle row" style={{ color: 'black', paddingTop: 15 }}>
                <div className="col-md-10 sm_device">
                    <i className={`${DELIVERY_ICON}`}></i> Delivery Trail
                </div>
                <div className="col-md-2 sm_device" style={{ textAlign: 'right' }}>
                    <i className={`${CALENDER_ICON}`}></i> {moment().format('DD MMM YYYY')}
                </div>
            </div>

            <div
                style={{ borderBottom: '1px solid lightgray', marginBottom: 15, marginTop: 15 }}
            />
            <div className="list-wrapper">
                <div id="bomTable" className="mt-4 border list-table-div bom-table-div delivery-trail-table" style={{ marginBottom: 50 }}>
                    
                        <ThemeProvider theme={theme}>
                            <MaterialTable
                                title=""
                                columns={columns}
                                data={trailList}
                                actions={[
                                    rowData => ({
                                        icon: () => <i className={`${EYE_ICON}`} title="View Summary"></i>,
                                        tooltip: 'View Summary',
                                        hidden: !rowData.isStarted,
                                        onClick: (event, rowData) => handleOpen(rowData)
                                    }),
                                    rowData => ({
                                        icon: () => <PlayCircleOutlineIcon color="primary" sx={{ fontSize: 24 }} />,
                                        tooltip: 'Start Delivery Trail',
                                        alignItems: 'center',
                                        hidden: rowData.isStarted,
                                        onClick: (event, rowData) => handleAction(rowData)
                                    }),
                                    rowData => ({
                                        icon: () => <i className={`${EDIT_ICON}`} title="Manage Delivery Trail"></i>,
                                        tooltip: 'Manage Delivery Trail',
                                        hidden: !rowData.isStarted,
                                        onClick: (event, rowData) => handleAction(rowData)
                                    }),
                                    
                                ]}
                                
                                options={{
                                    search: true,
                                    paging: true,
                                    pageSize: 10,
                                    pageSizeOptions: [10, 20, 50],
                                    actionsColumnIndex: -1,
                                    headerStyle: {
                                        textAlign: 'center'
                                    },
                                    rowStyle: { fontSize: 14 },
                                    cellStyle: {
                                        color: '#2d3d42',
                                        fontSize: 14,
                                    },
                                    exportButton: true,
                                    exportFileName: 'Delivery_Trail_List',
                                    actionsCellStyle: {
                                        display: 'table-cell', 
                                        verticalAlign: 'middle', 
                                        textAlign: 'center',     
                                        width: '1%',             
                                        whiteSpace: 'nowrap',    
                                        padding: '0 8px',        
                                    }
                                }}
                            />
                        </ThemeProvider>
                   
                </div>
            </div>

            {/* --- MINIMALIST MODAL --- */}
            <Dialog 
                open={isModalOpen} 
                onClose={handleClose}
                className="custom_modal_1"
                maxWidth="md"
                fullWidth
                PaperProps={{
                    elevation: 0,
                    sx: { borderRadius: 3, maxHeight: '80vh', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }
                }}
            >
                {selectedRowData && (
                    <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        
                        {/* Floating Close Button */}
                        <Box sx={{ position: 'absolute', right: 15, top: 15, zIndex: 10 }}>
                            <IconButton onClick={handleClose} size="small" sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#e0e0e0' } }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Grid container sx={{ flex: 1, height: '100%' }}>
                            
                            {/* LEFT COLUMN: Clean Details */}
                            <Grid item xs={12} md={4} sx={{ p: 4, borderRight: '1px solid #f5f5f5' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#2d3d42' }}>
                                    Project Summary
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
                                    Quick overview of the fixture status.
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <div>
                                        <Typography variant="overline" display="block" color="textSecondary" sx={{ lineHeight: 1 }}>Fixture Name</Typography>
                                        <Typography variant="subtitle2" sx={{ fontSize: '15px' }}>{selectedRowData.bom_id?.fixture_name}</Typography>
                                    </div>

                                    <div>
                                        <Typography variant="overline" display="block" color="textSecondary" sx={{ lineHeight: 1 }}>Customer</Typography>
                                        <Typography variant="subtitle2" sx={{ fontSize: '15px' }}>{selectedRowData.bom_id?.customer_id?.customer_name}</Typography>
                                    </div>

                                    <div>
                                        <Typography variant="overline" display="block" color="textSecondary" sx={{ lineHeight: 1 }}>Current Stage</Typography>
                                        <Chip 
                                            label={selectedRowData.current_step} 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: '#e0f7fa', 
                                                color: '#006064', 
                                                fontWeight: 600, 
                                                borderRadius: '4px', 
                                                height: 24, 
                                                fontSize: '11px',
                                                mt: 0.5
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <Typography variant="overline" display="block" color="textSecondary" sx={{ lineHeight: 1, mb: 0.5 }}>Completion</Typography>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={selectedRowData.progress || 0} 
                                            sx={{ height: 4, borderRadius: 2, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: '#2d3d42' } }}
                                        />
                                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>{Math.round(selectedRowData.progress)}% Completed</Typography>
                                    </div>
                                </Box>

                                <Button 
                                    variant="text" 
                                    fullWidth 
                                    sx={{ mt: 6, justifyContent: 'flex-start', color: '#2d3d42', pl: 0 }}
                                    endIcon={<ArrowForwardIcon fontSize="small"/>}
                                    onClick={() => handleAction(selectedRowData)}
                                >
                                    Open Full View
                                </Button>
                            </Grid>

                            {/* RIGHT COLUMN: Clean Timeline */}
                            <Grid item xs={12} md={8} sx={{ p: 4, overflowY: 'auto', maxHeight: '80vh' }}>
                                <Typography variant="overline" color="textSecondary" sx={{ mb: 3, display: 'block', letterSpacing: 1 }}>
                                    Activity Timeline
                                </Typography>

                                <Stepper orientation="vertical" nonLinear connector={<div style={{ width: '1px', height: '20px', backgroundColor: '#e0e0e0', marginLeft: '5px' }} />}>
                                    {selectedRowData.steps && selectedRowData.steps.map((step, index) => {
                                        const isCompleted = ['Completed', 'Skipped', 'Issue Reported'].includes(step.status);
                                        
                                        // Simple Logic to hide future pending steps to keep it clean, show only active/done
                                        const isActive = step.status === 'In Progress' || (step.status === 'Pending' && (index === 0 || selectedRowData.steps[index-1].status !== 'Pending'));
                                        if (!isCompleted && !isActive) return null; 

                                        return (
                                            <Step key={index} expanded={true} active={isActive} completed={isCompleted}>
                                                <StepLabel StepIconComponent={MinimalStepIcon} sx={{ py: 0 }}>
                                                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                                                        <Typography variant="subtitle2" sx={{ fontWeight: isActive ? 600 : 400, color: isActive ? '#000' : '#444' }}>
                                                            {step.name}
                                                        </Typography>
                                                        {step.completion_date && (
                                                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px' }}>
                                                                {moment(step.completion_date).format('MMM DD')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </StepLabel>
                                                <StepContent sx={{ borderLeft: '1px solid #e0e0e0', ml: '5px', pl: 3, py: 1 }}>
                                                    {step.remarks ? (
                                                        <Typography variant="body2" sx={{ color: '#555', fontSize: '13px', mb: 1 }}>
                                                            {step.remarks}
                                                        </Typography>
                                                    ) : (
                                                        isActive ? <Typography variant="caption" color="textSecondary">Waiting for update...</Typography> : null
                                                    )}

                                                    {step.attachments && step.attachments.length > 0 && (
                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                                            {step.attachments.map((file, i) => (
                                                                <Box 
                                                                    key={i} 
                                                                    component="a"
                                                                    href={file.file_url}
                                                                    target="_blank"
                                                                    sx={{ 
                                                                        display: 'flex', alignItems: 'center', 
                                                                        fontSize: '11px', color: '#666', 
                                                                        textDecoration: 'none',
                                                                        border: '1px solid #eee',
                                                                        borderRadius: '12px',
                                                                        px: 1, py: 0.2,
                                                                        '&:hover': { bgcolor: '#f9f9f9', color: '#000' }
                                                                    }}
                                                                >
                                                                    <AttachFileIcon sx={{ fontSize: 12, mr: 0.5 }} /> File {i + 1}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </StepContent>
                                            </Step>
                                        );
                                    })}
                                </Stepper>
                                
                                {(!selectedRowData.steps || selectedRowData.steps.length === 0) && (
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 4 }}>
                                        No activity recorded yet.
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </DialogContent>
                )}
            </Dialog>
        </div>
        ))

    );
};

export default DeliveryTrailDashboard;